import type { ReactNode } from 'react'
import { Flyout, FlyoutHeader } from '@/components/ui/flyout'
import { liftedTo, useFlyoutLayer, useRegisterOpenPanel, zIndexOf } from '@/components/ui/stacking-layer'
import { Button } from '@/components/ui/button'
import { Can } from '@/components/auth/Can'
import { Loader2 } from 'lucide-react'

export type FlyoutWidth = 'compact' | 'standard' | 'wide'
export const FLYOUT_WIDTHS: Record<FlyoutWidth, number> = { compact: 360, standard: 550, wide: 650 }

export interface CreateFlyoutAction {
  label: ReactNode
  onClick: () => void
  loading?: boolean
  disabled?: boolean
  perm?: string
}

export interface CreateFlyoutProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  width?: FlyoutWidth | number
  headerActions?: ReactNode
  primaryAction?: CreateFlyoutAction
  secondaryAction?: CreateFlyoutAction
  /** 底部左侧可选信息槽（如 upsell 的预估总计金额行），仅在使用结构化 primaryAction/secondaryAction 时生效 */
  footerInfo?: ReactNode
  footer?: ReactNode
  fitContent?: boolean
  children: ReactNode
}

function ActionButton({ action, variant }: { action: CreateFlyoutAction; variant: 'default' | 'outline' }) {
  const btn = (
    <Button variant={variant} onClick={action.onClick} disabled={action.disabled || action.loading}>
      {action.loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {action.label}
    </Button>
  )
  return action.perm ? <Can perm={action.perm}>{btn}</Can> : btn
}

/**
 * 全局创建中心统一侧边栏壳：永远 portal 到 body 的标准 Flyout + FlyoutHeader(仅 close)
 * + 结构化/raw/无 三态底栏。usePortal 内置，逃出顶栏 z-10 层叠上下文，不被列表 sticky 列头遮。
 */
export function CreateFlyout({
  open, onClose, title, width = 'standard', headerActions,
  primaryAction, secondaryAction, footerInfo, footer, fitContent = false, children,
}: CreateFlyoutProps) {
  const resolvedWidth = typeof width === 'number' ? width : FLYOUT_WIDTHS[width]
  // 登记面板的层（与 Flyout 的算法相同：默认 z-50，显式层下限只往上抬）：助手浮窗据此决定要不要沉到面板下面
  const floor = useFlyoutLayer()
  useRegisterOpenPanel(open, zIndexOf(liftedTo(undefined, floor)) ?? 50)

  let footerNode: ReactNode = null
  if (footer) {
    footerNode = footer
  } else if (primaryAction || secondaryAction || footerInfo) {
    footerNode = (
      <>
        <div className="flex items-center min-w-0">{footerInfo}</div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {secondaryAction && <ActionButton action={secondaryAction} variant="outline" />}
          {primaryAction && <ActionButton action={primaryAction} variant="default" />}
        </div>
      </>
    )
  }

  return (
    <Flyout
      open={open}
      onOpenChange={(o) => { if (!o) onClose() }}
      side="right"
      size="custom"
      width={resolvedWidth}
      noPadding
      showHeader={false}
      showBackdrop={false}
      usePortal
    >
      <FlyoutHeader
        title={title}
        onClose={onClose}
        showWatch={false}
        showShare={false}
        showOpenNew={false}
        actions={headerActions}
      />
      <div className={fitContent ? 'flex-1 flex flex-col overflow-hidden min-h-0' : 'flex-1 overflow-y-auto'}>
        {children}
      </div>
      {footerNode && (
        <div className="px-6 py-4 border-t border-border bg-card flex items-center justify-between gap-3 flex-shrink-0">
          {footerNode}
        </div>
      )}
    </Flyout>
  )
}
